/**
 * Публичный подбор привода по расчётной мощности (E14 / ФТ-12.2).
 * Полный каталог не отдаётся — только выбранная позиция (И-1 / НФТ-9).
 */

import { z } from "zod";
import { decimalStringSchema } from "./catalog";
import { apiSuccessSchema } from "./envelope";
import type { CalcPreviewOutput } from "./calc-preview";

export const publicDrivePreviewRequestSchema = z.object({
  requiredPowerKw: decimalStringSchema.refine((v) => Number(v) > 0, "Мощность должна быть > 0"),
  /** Модель → группа конвейеров для предпочтения бренда (AN-027). */
  modelId: z.string().uuid().optional(),
  groupId: z.string().uuid().nullable().optional(),
});
export type PublicDrivePreviewRequest = z.infer<typeof publicDrivePreviewRequestSchema>;

export const publicDrivePreviewItemSchema = z.object({
  brand: z.string().min(1).max(128),
  designation: z.string().min(1).max(255),
  powerKw: decimalStringSchema,
  rpm: decimalStringSchema.nullable(),
  torqueNm: decimalStringSchema.nullable(),
  brakeType: z.string().nullable(),
});
export type PublicDrivePreviewItem = z.infer<typeof publicDrivePreviewItemSchema>;

export const publicDrivePreviewResultSchema = z.object({
  drive: publicDrivePreviewItemSchema.nullable(),
});
export type PublicDrivePreviewResult = z.infer<typeof publicDrivePreviewResultSchema>;

export const publicDrivePreviewResultResponseSchema = apiSuccessSchema(
  publicDrivePreviewResultSchema,
);

export const PUBLIC_DRIVE_ERROR_CODES = {
  MODEL_NOT_FOUND: "PUBLIC_DRIVE_MODEL_NOT_FOUND",
  GROUP_NOT_FOUND: "PUBLIC_DRIVE_GROUP_NOT_FOUND",
} as const;
export type PublicDriveErrorCode =
  (typeof PUBLIC_DRIVE_ERROR_CODES)[keyof typeof PUBLIC_DRIVE_ERROR_CODES];

/**
 * Извлекает расчётную мощность привода из выходов preview.
 * Предпочтение: formulaName N / Nдв; иначе единица с «кВт».
 */
export function pickRequiredDrivePowerKw(
  outputs: readonly CalcPreviewOutput[],
): string | null {
  const byFormula = outputs.find(
    (row) => row.formulaName !== null && /^N(дв|dv)?$/i.test(row.formulaName),
  );
  if (byFormula && Number(byFormula.value) > 0) {
    return byFormula.value;
  }
  const byUnit = outputs.find(
    (row) => row.unitName !== null && /кВт/i.test(row.unitName) && Number(row.value) > 0,
  );
  return byUnit?.value ?? null;
}
